import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:     '#0A1628',
  navyHov:  '#111E35',
  gold:     '#C9A84C',
  black:    '#0A0A0A',
  white:    '#FFFFFF',
  surface:  '#F5F6F8',
  border:   '#D0D5DD',
  borderLt: '#E8EBF0',
  text:     '#0A0A0A',
  textSec:  '#3D4552',
  muted:    '#6B7280',
  mutedLt:  '#9CA3AF',
  success:  '#0A7C42',
  danger:   '#C0222A',
  amber:    '#92640A',
};

interface Firm { id: string; name: string; }
interface Fund  { id: string; name: string; }
interface Connection { id: string; status: string; allocator_firm: Firm; manager_firm: Firm; fund: Fund; }
interface Message { id: string; content: string; sender_id: string; created_at: string; read_at: string | null; }
interface Meeting {
  id: string; title: string; scheduled_at: string; duration_minutes: number;
  status: string; notes: string; proposed_by: string; recipient_firm_id: string;
  daily_room_url: string; connection_id: string; created_at: string;
  meeting_type: string; location: string;
}
interface RawConnection { id: string; status: string; allocator_firm: Firm | Firm[]; manager_firm: Firm | Firm[]; fund: Fund | Fund[]; }

function normalizeConnection(raw: RawConnection): Connection {
  return {
    id: raw.id, status: raw.status,
    allocator_firm: Array.isArray(raw.allocator_firm) ? raw.allocator_firm[0] : raw.allocator_firm,
    manager_firm:   Array.isArray(raw.manager_firm)   ? raw.manager_firm[0]   : raw.manager_firm,
    fund:           Array.isArray(raw.fund)           ? raw.fund[0]           : raw.fund,
  };
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white,
  border: `1px solid ${C.border}`, borderRadius: '3px',
  color: C.text, fontSize: '13px',
  padding: '8px 10px', outline: 'none',
  fontFamily: 'system-ui', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted,
  letterSpacing: '0.6px', textTransform: 'uppercase',
  display: 'block', marginBottom: '5px', fontFamily: 'system-ui',
};

export default function Messages() {
  const { user } = useAuth();
  const { firm } = useProfile();
  const [connections, setConnections]     = useState<Connection[]>([]);
  const [activeConnection, setActive]     = useState<Connection | null>(null);
  const [messages, setMessages]           = useState<Message[]>([]);
  const [meetings, setMeetings]           = useState<Meeting[]>([]);
  const [newMsg, setNewMsg]               = useState('');
  const [loading, setLoading]             = useState(true);
  const [responding, setResponding]       = useState<string | null>(null);
  const [showMeetingModal, setShowModal]  = useState(false);
  const [scheduling, setScheduling]       = useState(false);
  const [meetingError, setMeetingError]   = useState('');
  const [meetingForm, setMeetingForm]     = useState({
    title: '', date: '', time: '10:00',
    duration_minutes: 60, notes: '',
    meeting_type: 'video', location: '',
  });
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (firm) fetchConnections(); }, [firm]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, meetings]);

  useEffect(() => {
    if (!activeConnection) return;
    fetchMessages(activeConnection.id);
    fetchMeetings(activeConnection.id);
    const channel = supabase.channel(`messages-${activeConnection.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `connection_id=eq.${activeConnection.id}` },
        async (payload) => {
          const msg = payload.new as Message;
          setMessages(prev => [...prev, msg]);
          if (msg.sender_id !== user?.id) {
            await supabase.from('messages').update({ read_at: new Date().toISOString() }).eq('id', msg.id);
          }
        })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'meetings', filter: `connection_id=eq.${activeConnection.id}` },
        () => fetchMeetings(activeConnection.id))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeConnection]);

  const fetchConnections = async () => {
    if (!firm) return;
    const { data } = await supabase.from('connections')
      .select(`id, status,
        allocator_firm:firms!connections_allocator_firm_id_fkey(id, name),
        manager_firm:firms!connections_manager_firm_id_fkey(id, name),
        fund:funds(id, name)`)
      .eq('status', 'approved')
      .or(`allocator_firm_id.eq.${firm.id},manager_firm_id.eq.${firm.id}`);
    if (data?.length) {
      const normalized = (data as unknown as RawConnection[]).map(normalizeConnection);
      setConnections(normalized);
      setActive(normalized[0]);
    }
    setLoading(false);
  };

  const fetchMessages = async (cid: string) => {
    const { data } = await supabase.from('messages').select('*').eq('connection_id', cid).order('created_at', { ascending: true });
    if (data) setMessages(data);
    if (user) {
      await supabase.from('messages').update({ read_at: new Date().toISOString() })
        .eq('connection_id', cid).neq('sender_id', user.id).is('read_at', null);
    }
  };

  const fetchMeetings = async (cid: string) => {
    const { data } = await supabase.from('meetings').select('*').eq('connection_id', cid).order('created_at', { ascending: true });
    if (data) setMeetings(data);
  };

  const respondToMeeting = async (meetingId: string, accept: boolean) => {
    setResponding(meetingId);
    try {
      if (accept) {
        const meeting = meetings.find(m => m.id === meetingId);
        if (meeting?.meeting_type === 'video') {
          const roomName = `altveno-${Date.now()}`;
          const fnRes = await supabase.functions.invoke('create-daily-room', { body: { meeting_name: roomName, expires_in_hours: 72 } });
          if (fnRes.error) throw new Error(fnRes.error.message);
          await supabase.from('meetings').update({ status: 'confirmed', daily_room_url: fnRes.data.url, daily_room_name: fnRes.data.name }).eq('id', meetingId);
        } else {
          await supabase.from('meetings').update({ status: 'confirmed' }).eq('id', meetingId);
        }
      } else {
        await supabase.from('meetings').update({ status: 'cancelled' }).eq('id', meetingId);
      }
      await supabase.functions.invoke('handle-meeting-notification', { body: { meeting_id: meetingId, new_status: accept ? 'confirmed' : 'cancelled' } });
      if (activeConnection) fetchMeetings(activeConnection.id);
    } catch (err) { console.error(err); }
    setResponding(null);
  };

  const scheduleMeeting = async () => {
    if (!user || !activeConnection || !meetingForm.title || !meetingForm.date) { setMeetingError('Please fill in title and date.'); return; }
    if (meetingForm.meeting_type === 'in_person' && !meetingForm.location.trim()) { setMeetingError('Please enter a location.'); return; }
    setScheduling(true); setMeetingError('');
    try {
      const scheduledAt = new Date(`${meetingForm.date}T${meetingForm.time}`).toISOString();
      const recipientFirmId = activeConnection.allocator_firm?.id === firm?.id ? activeConnection.manager_firm?.id : activeConnection.allocator_firm?.id;
      const { error } = await supabase.from('meetings').insert({
        connection_id: activeConnection.id, proposed_by: user.id,
        recipient_firm_id: recipientFirmId, title: meetingForm.title,
        scheduled_at: scheduledAt, duration_minutes: meetingForm.duration_minutes,
        notes: meetingForm.notes, meeting_type: meetingForm.meeting_type,
        location: meetingForm.meeting_type === 'in_person' ? meetingForm.location : null, status: 'pending',
      });
      if (error) throw new Error(error.message);
      setShowModal(false);
      setMeetingForm({ title: '', date: '', time: '10:00', duration_minutes: 60, notes: '', meeting_type: 'video', location: '' });
      fetchMeetings(activeConnection.id);
    } catch (err: any) { setMeetingError(err.message); }
    setScheduling(false);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeConnection || !user) return;
    const content = newMsg.trim(); setNewMsg('');
    const { data } = await supabase.from('messages').insert({ connection_id: activeConnection.id, sender_id: user.id, content }).select().single();
    if (data) {
      setMessages(prev => [...prev, data]);
      logActivity(user.id, firm?.id ?? null, 'message_sent', 'message', data.id, { connection_id: activeConnection.id });
    }
  };

  const getOtherFirmName = (conn: Connection) =>
    conn.allocator_firm?.id === firm?.id ? conn.manager_firm?.name : conn.allocator_firm?.name;

  const fmt = (ts: string) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const fmtDate = (ts: string) => new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  type TLItem = { type: 'message'; data: Message; ts: string } | { type: 'meeting'; data: Meeting; ts: string };
  const timeline: TLItem[] = [
    ...messages.map(m => ({ type: 'message' as const, data: m, ts: m.created_at })),
    ...meetings.map(m  => ({ type: 'meeting'  as const, data: m, ts: m.created_at })),
  ].sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', background: C.surface, alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading messages...</div>
    </div>
  );

  if (!connections.length) return (
    <div style={{ display: 'flex', height: '100vh', background: C.surface, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px' }}>
      <div style={{ fontSize: '15px', color: C.text, fontFamily: 'Georgia, serif', fontWeight: 400 }}>No conversations yet</div>
      <div style={{ fontSize: '13px', color: C.muted, fontFamily: 'system-ui' }}>Messages appear here once a connection is approved</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* ── Conversation list ── */}
      <div style={{
        width: '260px', flexShrink: 0,
        borderRight: `1px solid ${C.border}`,
        display: 'flex', flexDirection: 'column',
        background: C.white,
      }}>
        <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, fontWeight: 400 }}>Messages</div>
          <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>{connections.length} conversation{connections.length !== 1 ? 's' : ''}</div>
        </div>
        {connections.map(conn => {
          const isActive = activeConnection?.id === conn.id;
          return (
            <div key={conn.id} onClick={() => setActive(conn)} style={{
              padding: '12px 14px', cursor: 'pointer',
              background: isActive ? '#EBF0FA' : C.white,
              borderBottom: `1px solid ${C.borderLt}`,
              borderLeft: isActive ? `3px solid ${C.navy}` : '3px solid transparent',
              transition: 'all 0.1s',
            }}
              onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = C.surface; }}
              onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = C.white; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '3px',
                  background: C.navy,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, color: C.gold, flexShrink: 0,
                  fontFamily: 'Georgia, serif',
                }}>
                  {getOtherFirmName(conn)?.charAt(0)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12.5px', fontWeight: isActive ? 600 : 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getOtherFirmName(conn)}
                  </div>
                  <div style={{ fontSize: '11px', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {conn.fund?.name}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Chat area ── */}
      {activeConnection && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Chat header */}
          <div style={{
            padding: '12px 20px', borderBottom: `1px solid ${C.border}`,
            background: C.white,
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '3px',
              background: C.navy,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '12px', fontWeight: 700, color: C.gold,
              fontFamily: 'Georgia, serif',
            }}>
              {getOtherFirmName(activeConnection)?.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>{getOtherFirmName(activeConnection)}</div>
              <div style={{ fontSize: '11px', color: C.muted }}>{activeConnection.fund?.name}</div>
            </div>
            <button
              onClick={() => { setMeetingError(''); setShowModal(true); }}
              style={{
                background: C.white, border: `1px solid ${C.border}`,
                borderRadius: '3px', color: C.textSec,
                fontSize: '11.5px', padding: '6px 12px',
                cursor: 'pointer', fontFamily: 'system-ui',
                display: 'flex', alignItems: 'center', gap: '5px',
                fontWeight: 500,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Schedule Meeting
            </button>
          </div>

          {/* Timeline */}
          <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', background: C.surface }}>
            {timeline.length === 0 && (
              <div style={{ textAlign: 'center', color: C.muted, fontSize: '13px', marginTop: '60px' }}>
                No messages yet, start the conversation.
              </div>
            )}

            {timeline.map((item, i) => {
              const showDate = i === 0 || fmtDate(timeline[i - 1].ts) !== fmtDate(item.ts);

              if (item.type === 'message') {
                const msg = item.data;
                const isMe = msg.sender_id === user?.id;
                return (
                  <React.Fragment key={`msg-${msg.id}`}>
                    {showDate && (
                      <div style={{ textAlign: 'center', fontSize: '10px', color: C.mutedLt, margin: '6px 0', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        {fmtDate(msg.created_at)}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '58%', padding: '10px 13px', borderRadius: '4px',
                        background: isMe ? C.navy : C.white,
                        color: isMe ? C.white : C.text,
                        fontSize: '13px', lineHeight: 1.55,
                        border: isMe ? 'none' : `1px solid ${C.border}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      }}>
                        <div>{msg.content}</div>
                        <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.55, textAlign: 'right', fontFamily: '"Courier New", monospace' }}>
                          {fmt(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }

              if (item.type === 'meeting') {
                const m = item.data;
                const isFromMe   = m.proposed_by === user?.id;
                const isPending  = m.status === 'pending';
                const isConfirmed = m.status === 'confirmed';
                const isCancelled = m.status === 'cancelled';
                const d = new Date(m.scheduled_at);
                const canRespond = isPending && !isFromMe && m.recipient_firm_id === firm?.id;
                const isVideo = m.meeting_type !== 'in_person';

                const statusColor  = isConfirmed ? C.success : isCancelled ? C.danger : C.amber;
                const statusBg     = isConfirmed ? '#F0FDF4' : isCancelled ? '#FEF2F2' : '#FFFBEB';
                const statusBorder = isConfirmed ? '#BBF7D0' : isCancelled ? '#FECACA' : '#FDE68A';
                const statusLabel  = isPending ? 'Meeting Request' : isConfirmed ? 'Meeting Confirmed' : 'Meeting Declined';

                return (
                  <React.Fragment key={`meeting-${m.id}`}>
                    {showDate && (
                      <div style={{ textAlign: 'center', fontSize: '10px', color: C.mutedLt, margin: '6px 0', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        {fmtDate(m.created_at)}
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <div style={{
                        width: '340px', background: statusBg,
                        border: `1px solid ${statusBorder}`,
                        borderRadius: '4px', padding: '14px 16px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={statusColor} strokeWidth="2" strokeLinecap="round">
                            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                          </svg>
                          <span style={{ fontSize: '10px', fontWeight: 700, color: statusColor, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                            {statusLabel}
                          </span>
                          <span style={{ fontSize: '10px', color: C.mutedLt, marginLeft: 'auto', fontFamily: '"Courier New", monospace' }}>
                            {fmt(m.created_at)}
                          </span>
                        </div>

                        <div style={{ fontSize: '13px', fontWeight: 600, color: C.black, marginBottom: '6px' }}>
                          {m.title}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '11.5px', color: C.textSec }}>
                            {d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} at {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} · {m.duration_minutes} min
                          </div>
                          <div style={{ fontSize: '11px', color: C.muted }}>
                            {isVideo ? 'Video call' : `In person · ${m.location || 'Location TBC'}`}
                          </div>
                          {m.notes && (
                            <div style={{ fontSize: '11px', color: C.muted, fontStyle: 'italic' }}>{m.notes}</div>
                          )}
                        </div>

                        {isFromMe && isPending && (
                          <div style={{ fontSize: '11px', color: C.amber, fontWeight: 600, letterSpacing: '0.3px' }}>
                            Awaiting response
                          </div>
                        )}

                        {canRespond && (
                          <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                            <button
                              onClick={() => respondToMeeting(m.id, false)}
                              disabled={responding === m.id}
                              style={{
                                flex: 1, background: C.white,
                                border: `1px solid ${C.danger}`,
                                borderRadius: '3px', color: C.danger,
                                fontSize: '11.5px', fontWeight: 600, padding: '7px',
                                cursor: 'pointer', fontFamily: 'system-ui',
                              }}
                            >Decline</button>
                            <button
                              onClick={() => respondToMeeting(m.id, true)}
                              disabled={responding === m.id}
                              style={{
                                flex: 1, background: C.navy, border: 'none',
                                borderRadius: '3px', color: C.white,
                                fontSize: '11.5px', fontWeight: 600, padding: '7px',
                                cursor: 'pointer', fontFamily: 'system-ui',
                                opacity: responding === m.id ? 0.7 : 1,
                              }}
                            >{responding === m.id ? '...' : 'Accept'}</button>
                          </div>
                        )}

                        {isConfirmed && isVideo && m.daily_room_url && (
                          <button
                            onClick={() => window.open(m.daily_room_url, '_blank')}
                            style={{
                              width: '100%', marginTop: '10px',
                              background: C.navy, border: 'none',
                              borderRadius: '3px', color: C.white,
                              fontSize: '11.5px', fontWeight: 600, padding: '8px',
                              cursor: 'pointer', fontFamily: 'system-ui',
                            }}
                          >Join Video Call →</button>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              }
              return null;
            })}
            <div ref={endRef} />
          </div>

          {/* Message input */}
          <div style={{
            padding: '12px 20px', borderTop: `1px solid ${C.border}`,
            background: C.white,
            display: 'flex', gap: '8px', alignItems: 'center',
          }}>
            <input
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Type a message..."
              style={{
                flex: 1, background: C.surface,
                border: `1px solid ${C.border}`, borderRadius: '3px',
                color: C.text, fontSize: '13px',
                padding: '9px 12px', outline: 'none',
                fontFamily: 'system-ui',
              }}
              onFocus={e => (e.target.style.borderColor = C.navy)}
              onBlur={e => (e.target.style.borderColor = C.border)}
            />
            <button onClick={sendMessage} style={{
              background: C.navy, border: 'none', borderRadius: '3px',
              color: C.white, fontSize: '12.5px', fontWeight: 600,
              padding: '9px 18px', cursor: 'pointer',
              fontFamily: 'system-ui', letterSpacing: '0.2px',
            }}>Send</button>
          </div>
        </div>
      )}

      {/* ── Schedule meeting modal ── */}
      {showMeetingModal && (
        <div onClick={() => setShowModal(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.white, border: `1px solid ${C.border}`,
            borderRadius: '4px', padding: '24px', width: '420px',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: '12px', marginBottom: '18px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '4px' }}>New Meeting Request</div>
              <div style={{ fontSize: '15px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>
                {activeConnection && getOtherFirmName(activeConnection)}
              </div>
            </div>

            {meetingError && (
              <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '3px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px', color: C.danger }}>
                {meetingError}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Meeting Title</label>
                <input value={meetingForm.title} onChange={e => setMeetingForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Introductory call, IDD review" style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input type="date" value={meetingForm.date} onChange={e => setMeetingForm(f => ({ ...f, date: e.target.value }))} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                </div>
                <div>
                  <label style={labelStyle}>Time</label>
                  <input type="time" value={meetingForm.time} onChange={e => setMeetingForm(f => ({ ...f, time: e.target.value }))} style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Duration</label>
                <select value={meetingForm.duration_minutes} onChange={e => setMeetingForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))}
                  style={{ ...inputStyle }}>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Meeting Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  {[{ value: 'video', label: 'Video Call' }, { value: 'in_person', label: 'In Person' }].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setMeetingForm(f => ({ ...f, meeting_type: opt.value }))} style={{
                      padding: '8px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer',
                      fontFamily: 'system-ui', fontWeight: 500,
                      border: `1px solid ${meetingForm.meeting_type === opt.value ? C.navy : C.border}`,
                      background: meetingForm.meeting_type === opt.value ? C.navy : C.white,
                      color: meetingForm.meeting_type === opt.value ? C.white : C.textSec,
                      transition: 'all 0.1s',
                    }}>{opt.label}</button>
                  ))}
                </div>
              </div>
              {meetingForm.meeting_type === 'in_person' && (
                <div>
                  <label style={labelStyle}>Location</label>
                  <input value={meetingForm.location} onChange={e => setMeetingForm(f => ({ ...f, location: e.target.value }))}
                    placeholder="e.g. 1 Canada Square, London" style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
                </div>
              )}
              <div>
                <label style={labelStyle}>Notes (optional)</label>
                <textarea value={meetingForm.notes} onChange={e => setMeetingForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Agenda, topics to cover..." rows={3} style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '18px' }}>
              <button onClick={() => setShowModal(false)} style={{
                background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
                color: C.muted, fontSize: '12.5px', padding: '9px', cursor: 'pointer', fontFamily: 'system-ui',
              }}>Cancel</button>
              <button onClick={scheduleMeeting} disabled={scheduling} style={{
                background: C.navy, border: 'none', borderRadius: '3px',
                color: C.white, fontSize: '12.5px', fontWeight: 600,
                padding: '9px', cursor: scheduling ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui', opacity: scheduling ? 0.7 : 1,
              }}>{scheduling ? 'Sending...' : 'Send Invite'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}